// GAVION booking (spec §2): free slots grouped by day and labelled in the VIEWER's time zone (never the rep's).
import { zoneLabel } from "../../../../../supabase/functions/_shared/booking/zoneLabel.ts";
import type { PublicLang } from "./copy";

export type SlotGroup = { key: string; label: string; times: { iso: string; label: string }[] };

export const browserZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export function timeLabel(iso: string, tz: string, lang: PublicLang): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0) % 24;
  const m = parts.find((p) => p.type === "minute")?.value ?? "00";
  if (lang === "fr") return `${h} h ${m}`;
  return `${h % 12 === 0 ? 12 : h % 12}:${m} ${h < 12 ? "a.m." : "p.m."}`;
}

export const dayLabel = (iso: string, tz: string, lang: PublicLang): string =>
  new Intl.DateTimeFormat(lang === "en" ? "en-CA" : "fr-CA", {
    timeZone: tz,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(iso));

export const whenLabel = (iso: string, tz: string, lang: PublicLang): string =>
  `${dayLabel(iso, tz, lang)} ${lang === "en" ? "at" : "à"} ${timeLabel(iso, tz, lang)}`;

/**
 * What a prospect reads for a zone: the city, not the IANA id (that one goes in a title attribute):
 * « heure de Toronto », « heure d'Edmonton », « heure de St. John's », "Toronto time"; UTC stays « heure UTC » / "UTC".
 * One source with the reminder email (final review M6).
 */
export { zoneLabel };

/** A time always shown with its zone (spec §2, §4): « jeudi 15 octobre à 11 h 15 (heure de Toronto) ». */
export const whenWithZone = (iso: string, tz: string, lang: PublicLang): string =>
  `${whenLabel(iso, tz, lang)} (${zoneLabel(tz, lang)})`;

export function groupSlots(slots: readonly string[], tz: string, lang: PublicLang): SlotGroup[] {
  const dayKey = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" });
  const groups = new Map<string, SlotGroup>();
  for (const iso of slots) {
    const key = dayKey.format(new Date(iso));
    const g = groups.get(key) ?? { key, label: dayLabel(iso, tz, lang), times: [] };
    g.times.push({ iso, label: timeLabel(iso, tz, lang) });
    groups.set(key, g);
  }
  return [...groups.values()];
}
